/**
 * @module app.shared
 */

import { bindingMode, autoinject, processContent, computedFrom } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import flatpickr from 'flatpickr';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { DateValue } from 'app/shared/lib/date-time';
import { updateMaterializeInputLabel } from 'app/shared/lib/materialize/dom-utilities';
import { guid } from 'app/shared/lib/guid';
import { getKey, Key } from 'app/shared/lib/keys';
import { microTask, defer } from 'app/shared/lib/decorators';
import { moveRulesToHelperSlot } from 'app/shared/elements/ess-rule';
import { IDateRangeInput } from 'app/shared/elements/forms/input-base';
import { stop } from 'app/shared/lib/noop';
import { parse } from 'date-fns';
import { deferEvent } from 'app/shared/lib/fire-events';
import { focusPrevious, focusNext } from 'app/shared/lib/focus';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { I18N } from 'aurelia-i18n';
@autoinject()
@processContent(moveRulesToHelperSlot)
export class EssDaterangeCustomElement implements IDateRangeInput {

    protected id: guid = guid();

    @bindable.stringNull({ defaultBindingMode: bindingMode.twoWay })
    public startDate: string | null = null;

    @bindable.stringNull({ defaultBindingMode: bindingMode.twoWay })
    public endDate: string | null = null;

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public minDate: string | null = null;

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public maxDate: string | null = null;

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public label: string | null = null;

    @bindable.stringNull()
    public requiredValidatorLabel: string | null = null;

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public placeholder: string | null = null;

    @bindable.string()
    public autocomplete: string = 'off';

    @bindable.booleanAttr()
    public required: boolean = false;

    @bindable.booleanAttr()
    public readonly: boolean = false;

    @bindable.booleanAttr()
    public disabled: boolean = false;

    @bindable.string()
    public testName: string = '';

    protected instance: flatpickr.Instance | null = null;
    protected isChanging: boolean = false;
    protected pickerId: string | null = null;
    protected onCloseFollowup: Function | null = null;

    protected deferEvent = deferEvent;

    ////****************** Element References ***************************************////
    protected readonly input!: HTMLInputElement;
    protected readonly element!: HTMLElement;
    protected readonly labelElement!: HTMLLabelElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly formatter: FormatProvider,
        private readonly i18n: I18N,
        protected readonly env: EnvironmentService,
        protected readonly logger: AppLogger) { }

    protected attached(): void {
        this.initializePicker();
        this.placeholderChanged();
    }

    protected detached(): void {
        this.destroyPicker();
    }

    ////****************** Events ***************************************************////
    protected startDateChanged(newValue: string | null, oldValue: string | null): void {
        if(this.instance && (newValue !== oldValue)) {
            this.updateValue();
        }
    }

    protected endDateChanged(newValue: string | null, oldValue: string | null): void {
        if(this.instance && (newValue !== oldValue)) {
            this.updateValue();
        }
    }

    protected placeholderChanged(): void {
        this.queueUpdateLabel();
    }

    protected handleKeyDown(event: KeyboardEvent): boolean {
        // improving the lives of keyboard users
        const key = getKey(event) as Key;
        if(key === Key.Enter || key === Key.Space || key === Key.Down) {
            event.stopPropagation();  // prevent flatpickr's handling of "enter" on the input (as well as default focusing behavior)
            this.open();
            return false;
        }
        else if(key === Key.Delete || key === Key.Backspace) {
            this.clearValue();
            return false;
        }
        return true;
    }

    protected handleInputMouseEvent(event: MouseEvent): boolean {
        event.stopPropagation();  // prevent flatpickr's handling of click or mousedown on the input
        this.toggle();
        return false;
    }

    protected handlePickerKeydown(event: KeyboardEvent): boolean {
        const key = getKey(event) as Key;
        // INFO [MW] Control tab key events on the flatpickr calender container, which has no tabable elements.
        // WARNING: The time picker portion of flatpickr does have tabable elements.
        if(key === Key.Tab) {
            event.preventDefault();
            event.stopPropagation();
            this.close();

            if(event.shiftKey) {
                focusPrevious(this.input);
            }
            else {
                focusNext(this.input);
            }
            return true;
        }
        return true;  // turn off Aurelia's automatic preventDefault() on events handled with delegate or trigger binding.
    }

    @computedFrom('label', 'requiredValidatorLabel')
    protected get requiredValueMessage() {
        return this.requiredValidatorLabel || this.i18n.tr('ui:validation:required', { what: this.i18n.tr(this.label || 'ui:labels.value') });
    }

    ////****************** Functions ************************************************////
    protected initializePicker() {

        // INFO [Matt] Called by the change handlers before element is attached,
        // so we need to check that the element is attached.
        if(!this.element) {
            return;
        }

        const minDate = parse(this.minDate || '', 'yyyy-MM-dd', 0);
        const maxDate = parse(this.maxDate || '', 'yyyy-MM-dd', 0);
        const onChange = this.onChange.bind(this);
        const onClose = this.onClose.bind(this);
        const onOpen = this.onOpen.bind(this);
        const formatDate = this.formatDate.bind(this);

        this.instance = flatpickr(this.element, {
            clickOpens: false,
            minDate: isNaN(Number(minDate)) ? undefined : minDate,
            maxDate: isNaN(Number(maxDate)) ? undefined : maxDate,
            mode: 'range',
            wrap: true,
            onOpen,
            onChange,
            onClose: [onChange, onClose],
            disableMobile: true,
            ariaDateFormat: 'aria',
            dateFormat: 'display',
            formatDate,
        });

        if(this.instance && this.instance.calendarContainer) {
            this.pickerId = this.instance.calendarContainer.getAttribute('id');
            if(!this.pickerId) {
                this.pickerId = `${this.id}-picker`;
                this.instance.calendarContainer.setAttribute('id', this.pickerId);
            }

            // Attach a listener to control when a user tabs out of the container
            this.instance.calendarContainer.addEventListener('keydown', this.handlePickerKeydown.bind(this), { capture: true });
        }

        this.updateValue();
    }

    protected destroyPicker() {
        if(this.instance) {
            if(this.instance.calendarContainer) {
                this.instance.calendarContainer.removeEventListener('keydown', this.handlePickerKeydown, { capture: true });
            }
            this.instance.destroy();
            this.instance = null;
        }
    }

    protected updateValue(): void {
        if(!this.isChanging && this.instance) {
            const startDateValue = new DateValue(this.startDate || '');
            const endDateValue = new DateValue(this.endDate || '');
            if(startDateValue.isValid() && endDateValue.isValid()) {
                const startDateParts = startDateValue.toParts();
                const nativeStart = new Date(startDateParts.year, startDateParts.month - 1, startDateParts.date);

                const endDateParts = endDateValue.toParts();
                const nativeEnd = new Date(endDateParts.year, endDateParts.month - 1, endDateParts.date);

                this.instance.setDate([nativeStart, nativeEnd], false); // don't fire change
            }
            else if(startDateValue.isValid()) {
                const startDateParts = startDateValue.toParts();
                const nativeStart = new Date(startDateParts.year, startDateParts.month - 1, startDateParts.date);
                this.instance.setDate([nativeStart, nativeStart], false); // don't fire change
            }
            else if(endDateValue.isValid()) {
                const endDateParts = endDateValue.toParts();
                const nativeEnd = new Date(endDateParts.year, endDateParts.month - 1, endDateParts.date);
                this.instance.setDate([nativeEnd, nativeEnd], false); // don't fire change
            }
            else {
                this.instance.clear(false); // don't fire change
            }

            this.queueUpdateLabel();
        }
    }

    protected open(): void {
        if(this.instance && !this.readonly) {
            this.instance.open();
        }
    }

    protected close(): void {
        if(this.instance && !this.readonly) {
            this.instance.close();
        }
    }

    protected toggle(): void {
        if(this.instance && !this.readonly) {
            if(this.instance.isOpen) {
                this.onCloseFollowup = () => {
                    // Transition focus back to the input, when a UIEvent on the input causes the close
                    this.input.focus({ preventScroll: true });
                }
            }
            this.instance.toggle();
        }
    }

    protected clearValue(): void {
        if(this.readonly) { return; }
        if(this.instance) {
            this.instance.clear(false);
            this.instance.close();
        }
        this.startDate = null;
        this.endDate = null;
    }

    @microTask
    protected synchronizeValues(start: string | null, end: string | null): void {
        // bindable values update asynchronously, so run this in a microtask
        try {
            // prevent infinite recursion
            this.isChanging = true;
            this.startDate = start;
            this.endDate = end;
        }
        finally {
            this.isChanging = false;
        }
    }

    @defer
    protected queueUpdateLabel(): void {
        updateMaterializeInputLabel(this.input, this.labelElement);
    }

    protected stop(event: Event) {
        if(this.readonly) {
            stop(event);
        }
        return true
    };

    protected formatDate(date: Date, format: string): string {
        const value = DateValue.fromParts({
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            date: date.getDate()
        });
        const options: any = {};
        if(format === 'aria') {
            options.format = 'aria';
        }
        return this.formatter.formatDate(value, options) || '';
    }

    public normalizeDateRange(): void {
        try {
            this.isChanging = true;

            const [start, end] = [this.startDate, this.endDate];
            if(start && !end) {
                this.endDate = start;
            }
            else if(end && !start) {
                this.startDate = end;
            }
            else if(start && end) {
                if(start > end) {
                    this.endDate = start;
                    this.startDate = end;
                }
            }
        }
        finally {
            this.isChanging = false;
            this.updateValue();
        }
    }

    ////****************** Flatpickr Event Hooks ************************************************////
    protected onChange(selectedDates: Date[], dateStr: string, instance: flatpickr.Instance): void {
        let [from, to] = selectedDates;
        switch(selectedDates.length) {
            case 0:
                this.synchronizeValues(null, null);
                break;
            case 1:
                const value = DateValue.fromParts({
                    year: from.getFullYear(),
                    month: from.getMonth() + 1,
                    date: from.getDate()
                }).toJSON();
                this.synchronizeValues(value, null);
                break;
            default:
                const start = DateValue.fromParts({
                    year: from.getFullYear(),
                    month: from.getMonth() + 1,
                    date: from.getDate()
                }).toJSON();
                const end = DateValue.fromParts({
                    year: to.getFullYear(),
                    month: to.getMonth() + 1,
                    date: to.getDate()
                }).toJSON();
                this.synchronizeValues(start, end);
                break;
        }

        this.queueUpdateLabel();
    }

    protected onOpen(): void {
        this.input.classList.add('ess-open');
        this.input.setAttribute('aria-expanded', 'true');

        if(this.instance) {
            // move label out of way when popup opens
            if(!this.startDate && !this.endDate && !this.placeholder) {
                this.input.setAttribute('placeholder', ' ');
            }

            // set focus to the calendar (selected day and current day elements are prioritized)
            const days = this.instance.days;
            if(days) {
                // TODO [Matt] Need to cast as an any to allow building on the build server.
                const selectedDay = <any>days.querySelector('.flatpickr-day.selected:not(.flatpickr-disabled)');
                const today = <any>days.querySelector('.flatpickr-day.today:not(.flatpickr-disabled)');
                const firstAvailableDay = <any>days.querySelector('.flatpickr-day:not(.flatpickr-disabled)');
                if (selectedDay) {
                    selectedDay.focus({ preventScroll: true });
                } else if (today) {
                    today.focus({ preventScroll: true });
                } else if (firstAvailableDay) {
                    firstAvailableDay.focus({ preventScroll: true });
                } else if (this.instance.calendarContainer) {
                    (<any>this.instance.calendarContainer).focus({ preventScroll: true });
                }
            }
        }
    }

    protected onClose(): void {
        this.input.classList.remove('ess-open');
        this.input.setAttribute('aria-expanded', 'false');

        if(!this.placeholder) {
            // return label to original place
            this.input.setAttribute('placeholder', '');
        }

        if(this.onCloseFollowup) {
            this.onCloseFollowup();
            this.onCloseFollowup = null;
        }
    }

}
